import React, { useCallback, useEffect, useState } from 'react'
import _ from 'underscore'
import { useNavigate } from 'react-router-dom'

import SiteMessage from 'models/SiteMessage'

import Form from 'pages/shared/Form'
import BrandFields from 'pages/shared/SiteMessageForm/BrandFields'
import RoleFields from 'pages/shared/SiteMessageForm/RoleFields'
import TierFields from 'pages/shared/SiteMessageForm/TierFields'
import MessageTypeFields from 'pages/shared/SiteMessageForm/MessageTypeFields'
import SiteBannerFields from 'pages/shared/SiteMessageForm/SiteBannerFields'
import MessageFields from 'pages/shared/SiteMessageForm/MessageFields'
import ActionButtonFields from 'pages/shared/SiteMessageForm/ActionButtonFields'
import UpdatesPageFields from 'pages/shared/SiteMessageForm/UpdatesPageFields'

import { useStoreContext } from 'hooks/StoreContext'
import Api from 'services/api'

import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type SiteMessageAttributes = {
  id: number | null
  brands: string[]
  roles: string[]
  tiers: string[]
  siteBannerEnabled: boolean
  imageType: string
  messageType: string
  published: boolean
  publishedAt: string | null
  image?: string | null
  title?: string
  message?: string
  actionButtonCaption?: string
  actionButtonLink?: string
}

type SiteMessageModel = SiteMessageAttributes & {
  save: (
    updatedSiteMessageAttributes: SiteMessageAttributes,
    onSaveSuccessCallback: () => void,
    onSaveFailCallback: () => void,
    onValidationErrorCallback: (errors: FormErrors) => void
  ) => void
  delete: (
    onDeleteSuccessCallback: () => void,
    onDeleteFailCallback: () => void
  ) => void
}

type FeatureTourSummary = {
  title: string
}

type FormErrors = Record<string, string>

type SiteMessageFormProps = {
  siteMessage?: SiteMessageModel
}

const ISiteMessage = new SiteMessage() as unknown as SiteMessageModel

const SiteMessageForm = ({ siteMessage = ISiteMessage }: SiteMessageFormProps) => {
  const navigate = useNavigate()
  const { setAreSiteMessagesLoaded } = useStoreContext()

  const [siteMessageAttributes, setSiteMessageAttributes] = useState<SiteMessageAttributes>({ ...siteMessage })
  const [featureTours, setFeatureTours] = useState<FeatureTourSummary[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()
  const componentType = 'Site Message'

  useEffect(() => {
    if (siteMessage.id != null) {
      Api.SiteMessages.toursForSiteMessage(siteMessage.id)
        .then((res: FeatureTourSummary[]) => {
          setFeatureTours(res)
        })
        .catch(() => {
          setFeatureTours([])
        })
    } else {
      setFeatureTours([])
    }
  }, [siteMessage.id]) //

  const compareArrays = (arr1: string[], arr2: string[]) => arr1.length === arr2.length && arr1.every(i => arr2.includes(i))

  const onSaveCallback = useCallback(async () => {
    let featureConfirm = true
    const hasRoleChange = !compareArrays(siteMessageAttributes.roles, siteMessage.roles)
    const hasBrandChange = !compareArrays(siteMessageAttributes.brands, siteMessage.brands)
    if (featureTours.length > 0 && (hasRoleChange || hasBrandChange)) {
      const featureTourTitles = featureTours.map((tour) => tour.title).join(', ')
      const isConfirmed = await confirm({
        title: 'Confirm message change',
        description: `Changing the message will remove the following feature tour(s): ${featureTourTitles}. Are you sure you want to change the message?`,
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.CONFIRM
      })

      featureConfirm = isConfirmed
    }

    if (featureConfirm) {
      setIsSaving(true)

      const onSaveSuccessCallback = () => {
        // TODO: it would be nice for the user experience to stay on the edit page while both
        // deleting the record and refetching the list, then navigating to a list that instantly loads,
        // instead of navigating to the list and seeing a page spinner there.

        setAreSiteMessagesLoaded(false)
        navigate('/admin/site_messages')
      }

      const onValidationErrorCallback = (validationErrors: FormErrors) => {
        setErrors(validationErrors)

        setIsSaving(false)
      }

      const onSaveFailureCallBack = () => {
        setIsSaving(false)
        alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.SAVE_FAILED, description: '' })
      }

      siteMessage.save(siteMessageAttributes, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
    }
  }, [navigate, siteMessage, siteMessageAttributes, setAreSiteMessagesLoaded, featureTours]) // eslint-disable-line react-hooks/exhaustive-deps

  const onCancelCallback = useCallback(async () => {
    const hasChanges = !_.isEqual(siteMessageAttributes, { ...siteMessage })

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })

    if (!hasChanges || (hasChanges && isConfirmed)) {
      navigate('/admin/site_messages')
    }
  }, [navigate, siteMessage, siteMessageAttributes]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDeleteCallback = useCallback(async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        // TODO: it would be nice for the user experience to stay on the edit page while both
        // deleting the record and refetching the list, then navigating to a list that instantly loads,
        // instead of navigating to the list and seeing a page spinner there.

        setAreSiteMessagesLoaded(false)
        navigate('/admin/site_messages')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)
        alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.DELETE_FAILED, description: '' })
      }

      // TODO: change signature to accomodate named anonymous functions
      // siteMessage.delete({ onDeleteSuccessCallback: () => .., onDeleteFailureCallback: () => ...)
      siteMessage.delete(onDeleteSuccessCallback, onDeleteFailureCallback)
    }
  }, [navigate, siteMessage, setAreSiteMessagesLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSiteMessageUpdateCallback = useCallback((updatedAttributes: Partial<SiteMessageAttributes>) => {
    setSiteMessageAttributes({ ...siteMessageAttributes, ...updatedAttributes })
  }, [siteMessageAttributes])

  return (
    <Form
      deleteButtonText="Message"
      isSaving={isSaving}
      isDeleting={isDeleting}
      displayDeleteButton={siteMessageAttributes.id !== null}
      onSaveCallback={onSaveCallback}
      onDeleteCallback={onDeleteCallback}
      onCancelCallback={onCancelCallback}
    >
      <BrandFields
        selectedBrands={siteMessageAttributes.brands}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <RoleFields
        selectedRoles={siteMessageAttributes.roles}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <TierFields
        selectedTiers={siteMessageAttributes.tiers}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <MessageTypeFields
        messageType={siteMessageAttributes.messageType}
        onChangeCallback={onSiteMessageUpdateCallback}
      />

      <SiteBannerFields
        enabled={siteMessageAttributes.siteBannerEnabled}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />

      <UpdatesPageFields
        published={siteMessageAttributes.published}
        publishedAt={siteMessageAttributes.publishedAt || ''}
        onChangeCallback={onSiteMessageUpdateCallback}
      />

      <MessageFields
        imageType={siteMessageAttributes.imageType}
        image={siteMessageAttributes.image || undefined}
        title={siteMessageAttributes.title}
        message={siteMessageAttributes.message}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />
      <br />
      <ActionButtonFields
        caption={siteMessageAttributes.actionButtonCaption}
        link={siteMessageAttributes.actionButtonLink}
        onChangeCallback={onSiteMessageUpdateCallback}
        errors={errors}
      />
    </Form>
  )
}

export default SiteMessageForm
